import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';

const SUBMIT_URL = 'https://readdy.ai/api/form/d6j51f7gl4s4nd8dloag';

const guideChapters = [
  {
    icon: 'ri-heart-pulse-line',
    color: 'bg-rose-50 text-rose-500',
    title: 'Chapter 1: Nutrition & Feeding',
    desc: 'What to feed, how often, wet vs dry food, and the ingredients to always avoid.',
  },
  {
    icon: 'ri-stethoscope-line',
    color: 'bg-teal-50 text-teal-500',
    title: 'Chapter 2: Health & Vet Care',
    desc: 'Vaccination schedules, signs of illness, dental care, and when to rush to the vet.',
  },
  {
    icon: 'ri-home-smile-line',
    color: 'bg-amber-50 text-amber-500',
    title: 'Chapter 3: Home & Environment',
    desc: 'Cat-proofing your home, litter box setup, scratching posts, and safe plants.',
  },
  {
    icon: 'ri-gamepad-line',
    color: 'bg-violet-50 text-violet-500',
    title: 'Chapter 4: Play & Enrichment',
    desc: 'Best toys by age, how to prevent boredom, and building a stimulating environment.',
  },
  {
    icon: 'ri-emotion-happy-line',
    color: 'bg-sky-50 text-sky-500',
    title: 'Chapter 5: Behavior & Training',
    desc: 'Understanding cat body language, stopping bad habits, and positive reinforcement.',
  },
  {
    icon: 'ri-scissors-line',
    color: 'bg-pink-50 text-pink-500',
    title: 'Chapter 6: Grooming & Hygiene',
    desc: 'Brushing routines, nail trimming, bathing tips, and ear & eye care.',
  },
];

const testimonials = [
  {
    name: 'Sarah M.',
    location: 'Austin, TX',
    avatar: 'https://public.readdy.ai/ai/img_res/19d6da5c0402304a3c37dbd3980cdc74.jpg',
    text: 'This guide literally saved my kitten\'s life — I had no idea certain houseplants were toxic. Every new cat owner needs this!',
    stars: 5,
  },
  {
    name: 'James K.',
    location: 'Portland, OR',
    avatar: 'https://readdy.ai/api/search-image?query=friendly%20smiling%20young%20man%20portrait%2C%20casual%20style%2C%20warm%20lighting%2C%20soft%20background&width=80&height=80&seq=testi-2&orientation=squarish',
    text: 'The nutrition chapter alone is worth it. I switched my cats to a better diet and their coats are incredible now.',
    stars: 5,
  },
  {
    name: 'Priya L.',
    location: 'Chicago, IL',
    avatar: 'https://readdy.ai/api/search-image?query=friendly%20smiling%20woman%20with%20glasses%20portrait%2C%20natural%20lighting%2C%20soft%20neutral%20background&width=80&height=80&seq=testi-3&orientation=squarish',
    text: 'Clear, practical, and not overwhelming. I\'ve read 3 cat books and this free guide beats all of them.',
    stars: 5,
  },
];

const stats = [
  { value: '12,400+', label: 'Cat Lovers Downloaded' },
  { value: '6', label: 'In-Depth Chapters' },
  { value: '47', label: 'Expert Tips Inside' },
  { value: '100%', label: 'Free, Always' },
];

const catCareSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Free Ultimate Cat Care Guide - MeowCentral',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/cat-care-guide`,
    description: 'Download the free Ultimate Cat Care Guide — 47 expert tips across 6 chapters covering nutrition, health, behavior, grooming, and more. Written by vets and cat behaviorists.',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Care for Your Cat: The Complete Guide',
    description: '47 expert tips across 6 chapters covering everything you need to raise a happy, healthy cat.',
    step: [
      { '@type': 'HowToStep', name: 'Nutrition & Feeding', text: 'Learn what to feed, how often, wet vs dry food, and ingredients to avoid.' },
      { '@type': 'HowToStep', name: 'Health & Vet Care', text: 'Vaccination schedules, signs of illness, dental care, and when to visit the vet.' },
      { '@type': 'HowToStep', name: 'Home & Environment', text: 'Cat-proof your home, set up litter boxes, scratching posts, and safe plants.' },
      { '@type': 'HowToStep', name: 'Play & Enrichment', text: 'Best toys by age, preventing boredom, and building a stimulating environment.' },
      { '@type': 'HowToStep', name: 'Behavior & Training', text: 'Understand cat body language, stop bad habits, and use positive reinforcement.' },
      { '@type': 'HowToStep', name: 'Grooming & Hygiene', text: 'Brushing routines, nail trimming, bathing tips, and ear & eye care.' },
    ],
  },
];

export default function CatCareGuidePage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus('submitting');
    try {
      const params = new URLSearchParams();
      params.append('email', email);
      params.append('name', name);
      params.append('source', 'cat_care_guide_page');
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Free Cat Care Guide - 47 Expert Tips for Happy Healthy Cats | MeowCentral"
        description="Download the free Ultimate Cat Care Guide — 47 expert tips across 6 chapters on nutrition, health, behavior, grooming & more. Written by vets. Loved by 12,400+ cat owners."
        keywords="cat care guide, cat care tips, how to care for a cat, cat health guide, kitten care, cat nutrition, cat behavior, free cat guide"
        canonical="/cat-care-guide"
        schema={catCareSchema}
      />
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=cozy%20home%20library%20scene%20with%20a%20fluffy%20orange%20tabby%20cat%20sitting%20on%20a%20stack%20of%20books%2C%20warm%20golden%20hour%20sunlight%20streaming%20through%20window%2C%20soft%20bokeh%20background%2C%20rich%20amber%20and%20cream%20tones%2C%20professional%20lifestyle%20photography%2C%20inviting%20and%20warm%20atmosphere&width=1440&height=700&seq=guide-hero-bg&orientation=landscape"
            alt="Cat with books"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 flex flex-col lg:flex-row items-center gap-12">
          {/* Left copy */}
          <div className="flex-1 text-white">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-500/90 text-white text-xs font-bold rounded-full mb-5 uppercase tracking-wide">
                <i className="ri-gift-2-line"></i> 100% Free Download
              </span>
              <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-5">
                The Ultimate<br />
                <span className="text-teal-400">Cat Care Guide</span><br />
                Every Owner Needs
              </h1>
              <p className="text-white/80 text-lg mb-8 max-w-lg leading-relaxed">
                47 expert tips across 6 chapters — covering nutrition, health, behavior, grooming & more. Written by vets and cat behaviorists. Completely free.
              </p>

              {/* Stats row */}
              <div className="flex flex-wrap gap-6">
                {stats.map((s) => (
                  <div key={s.label}>
                    <div className="text-2xl font-bold text-teal-400">{s.value}</div>
                    <div className="text-white/60 text-xs">{s.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right form card */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
          >
            {/* Guide cover preview */}
            <div className="relative h-40 bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center overflow-hidden">
              <div className="absolute inset-0 opacity-20">
                <img
                  src="https://readdy.ai/api/search-image?query=cute%20kittens%20pattern%20seamless%20background%2C%20pastel%20teal%2C%20playful%20cartoon%20style&width=400&height=200&seq=guide-cover-bg&orientation=landscape"
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative z-10 text-center text-white px-6">
                <div className="text-4xl mb-2">📖</div>
                <div className="font-bold text-lg leading-tight">The Ultimate Cat Care Guide</div>
                <div className="text-teal-200 text-xs mt-1">MeowCentral • 2025 Edition</div>
              </div>
              <div className="absolute top-3 right-3 bg-amber-400 text-amber-900 text-xs font-bold px-2 py-1 rounded-full">
                FREE
              </div>
            </div>

            <div className="p-7">
              <AnimatePresence mode="wait">
                {status === 'success' ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-4"
                  >
                    <div className="text-5xl mb-3">🎉</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Check Your Inbox!</h3>
                    <p className="text-gray-500 text-sm mb-5">
                      Your <strong>Free Cat Care Guide</strong> is on its way. Also check your spam folder just in case.
                    </p>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-home-4-line"></i> Back to MeowCentral
                    </Link>
                  </motion.div>
                ) : (
                  <motion.div key="form" initial={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <h2 className="text-xl font-bold text-gray-900 mb-1">Get Your Free Copy</h2>
                    <p className="text-gray-500 text-sm mb-5">Instant delivery to your inbox. No spam, ever.</p>

                    <form
                      data-readdy-form
                      onSubmit={handleSubmit}
                      className="space-y-3"
                    >
                      <input
                        type="text"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your first name"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                      />
                      <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        placeholder="Your email address"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                      />
                      {status === 'error' && (
                        <p className="text-rose-500 text-xs">Something went wrong. Please try again.</p>
                      )}
                      <button
                        type="submit"
                        disabled={status === 'submitting'}
                        className="w-full py-3.5 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-200 text-white font-bold rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-lg shadow-teal-200"
                      >
                        {status === 'submitting' ? (
                          <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                        ) : (
                          <><i className="ri-download-2-line"></i> Send Me the Free Guide!</>
                        )}
                      </button>
                    </form>

                    <div className="flex items-center justify-center gap-4 mt-4">
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <i className="ri-lock-line text-teal-400"></i> No spam
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <i className="ri-close-circle-line text-teal-400"></i> Unsubscribe anytime
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <i className="ri-shield-check-line text-teal-400"></i> 100% free
                      </span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── WHAT'S INSIDE ── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs font-bold rounded-full mb-3 uppercase tracking-wide">
              What's Inside
            </span>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">6 Chapters. 47 Expert Tips.</h2>
            <p className="text-gray-500 max-w-xl mx-auto text-sm">
              Everything you need to raise a happy, healthy cat — from kitten to senior years.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {guideChapters.map((ch, i) => (
              <motion.div
                key={ch.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100"
              >
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${ch.color} mb-4`}>
                  <i className={`${ch.icon} text-xl`}></i>
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-2">{ch.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{ch.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BONUS SECTION ── */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl p-10 flex flex-col lg:flex-row items-center gap-10 text-white overflow-hidden relative">
            <div className="absolute right-0 top-0 opacity-10 text-[200px] leading-none select-none">🐾</div>
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 text-white text-xs font-bold rounded-full mb-4 uppercase">
                <i className="ri-star-line"></i> Bonus Included
              </div>
              <h3 className="text-2xl font-bold mb-3">Plus 3 Free Bonus Resources</h3>
              <ul className="space-y-3">
                {[
                  { icon: 'ri-file-list-3-line', text: 'Printable Vet Visit Checklist' },
                  { icon: 'ri-calendar-check-line', text: 'Monthly Cat Health Tracker Template' },
                  { icon: 'ri-plant-line', text: 'Toxic Plants & Foods Quick-Reference Card' },
                ].map((b) => (
                  <li key={b.text} className="flex items-center gap-3 text-sm text-white/90">
                    <div className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-lg shrink-0">
                      <i className={`${b.icon} text-sm`}></i>
                    </div>
                    {b.text}
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full lg:w-72 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-center mb-4">
                <div className="text-3xl mb-1">🎁</div>
                <div className="font-bold text-lg">All Yours — Free</div>
                <div className="text-white/70 text-xs mt-1">Enter your email above to unlock everything</div>
              </div>
              <div className="space-y-2">
                {['Cat Care Guide (PDF)', 'Vet Checklist', 'Health Tracker', 'Toxic Plants Card'].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-white/90">
                    <i className="ri-checkbox-circle-fill text-teal-200"></i>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SOCIAL PROOF ── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Loved by 12,400+ Cat Owners</h2>
            <p className="text-gray-500 text-sm">Real reviews from real cat parents</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
              >
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.stars }).map((_, si) => (
                    <i key={si} className="ri-star-fill text-amber-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-5">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover object-top"
                  />
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{t.name}</div>
                    <div className="text-gray-400 text-xs">{t.location}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA ── */}
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="text-5xl mb-4">🐱</div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Ready to Become the Best Cat Parent?</h2>
          <p className="text-gray-500 text-sm mb-8">
            Join over 12,400 cat lovers who already downloaded the guide. It takes 10 seconds and it's completely free.
          </p>

          <AnimatePresence mode="wait">
            {status === 'success' ? (
              <motion.div
                key="cta-success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-teal-50 border border-teal-200 rounded-2xl p-8"
              >
                <div className="text-4xl mb-3">✅</div>
                <h3 className="text-xl font-bold text-teal-800 mb-2">You're all set!</h3>
                <p className="text-teal-700 text-sm">Check your inbox for the guide. Welcome to the Meow Squad!</p>
              </motion.div>
            ) : (
              <motion.form
                key="cta-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Enter your email address"
                  className="flex-1 border border-gray-200 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="px-6 py-3.5 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-200 text-white font-bold rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-lg shadow-teal-200"
                >
                  {status === 'submitting' ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                  ) : (
                    <><i className="ri-download-2-line"></i> Get It Free</>
                  )}
                </button>
              </motion.form>
            )}
          </AnimatePresence>

          <p className="text-gray-400 text-xs mt-4">
            No spam. Unsubscribe anytime. Your email is safe with us.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
